import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { sortRows, SortableTh, toggleSort } from '../utils/sortTable';

const emptyDetails = {
  manufacturer: '', manufacturer_part_number: '', package_type: '', material: '',
  weight: '', dimensions: '', voltage_rating: '', tolerance: '', lifecycle_status: '',
  datasheet_url: '', revision: '', notes: '',
};

const emptyComponent = { component_product_id: '', quantity: 1, unit: '', reference_designator: '', sort_order: 0, notes: '' };

// Parça kodları Latince: "I" ile "ı" farkı aramayı bozmasın
const normalizeSearch = (text) => (text || '').toLocaleLowerCase('tr').replace(/ı/g, 'i');

function ComponentTree({ nodes }) {
  if (!nodes || nodes.length === 0) return null;
  return (
    <ul className="pl-4 border-l">
      {nodes.map(node => (
        <li key={node.id} className="mt-1">
          <div className="flex items-center gap-2 text-sm">
            {node.is_assembly && <span className="text-purple-500">🧩</span>}
            <span className="font-mono text-gray-500">{node.product_code}</span>
            <span className="text-gray-800">{node.product_name}</span>
            <span className="text-gray-400">× {node.quantity} {node.unit}</span>
            {node.reference_designator && <span className="text-gray-400">({node.reference_designator})</span>}
          </div>
          <ComponentTree nodes={node.children} />
        </li>
      ))}
    </ul>
  );
}

export default function AssemblyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [details, setDetails] = useState(emptyDetails);
  const [components, setComponents] = useState([]);
  const [tree, setTree] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [showComponentForm, setShowComponentForm] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [componentForm, setComponentForm] = useState(emptyComponent);
  const [componentCategoryFilter, setComponentCategoryFilter] = useState('');
  const [ozdisanCode, setOzdisanCode] = useState('');
  const [showCloneForm, setShowCloneForm] = useState(false);
  const [cloneForm, setCloneForm] = useState({ code: '', name: '' });
  const [cloning, setCloning] = useState(false);
  const [componentSearch, setComponentSearch] = useState('');
  const [componentSort, setComponentSort] = useState({ key: null, dir: 'asc' });

  const productCategories = [...new Set(allProducts.map(p => p.category))].sort();
  const componentCandidates = componentCategoryFilter
    ? allProducts.filter(p => p.category === componentCategoryFilter)
    : allProducts;

  const q = normalizeSearch(componentSearch.trim());
  const filteredComponents = components.filter(c =>
    [c.product_code, c.product_name, c.reference_designator].some(v => normalizeSearch(v).includes(q))
  );
  const sortedComponents = sortRows(filteredComponents, componentSort);
  const toggleComponentSort = (key) => toggleSort(setComponentSort, key);

  const fetchAll = useCallback(async () => {
    const [productRes, detailsRes, componentsRes, treeRes, productsRes] = await Promise.all([
      api.get(`/api/products/${id}`),
      api.get(`/api/bom/products/${id}/details`),
      api.get(`/api/bom/assemblies/${id}/components`),
      api.get(`/api/bom/assemblies/${id}/tree`),
      api.get('/api/products/'),
    ]);
    setProduct(productRes.data);
    setDetails(detailsRes.data ? { ...emptyDetails, ...detailsRes.data } : emptyDetails);
    setComponents(componentsRes.data);
    setTree(treeRes.data);
    setAllProducts(productsRes.data.filter(p => p.id !== parseInt(id)));
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { setComponentSearch(''); }, [id]);

  const handleExcelDownload = async () => {
    try {
      const res = await api.get(`/api/bom/assemblies/${id}/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${product.code}_BOM.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Excel indirilemedi');
    }
  };

  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...details, weight: details.weight === '' ? null : parseFloat(details.weight) };
      await api.put(`/api/bom/products/${id}/details`, payload);
      fetchAll();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const openCloneForm = () => {
    setCloneForm({ code: `${product.code}-COPY`, name: `${product.name} (Kopya)` });
    setShowCloneForm(true);
  };

  const handleCloneSubmit = async (e) => {
    e.preventDefault();
    setCloning(true);
    try {
      const res = await api.post(`/api/bom/assemblies/${id}/clone`, cloneForm);
      setShowCloneForm(false);
      navigate(`/assemblies/${res.data.id}`);
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setCloning(false);
    }
  };

  const fetchOzdisanCode = async (productId) => {
    if (!productId) { setOzdisanCode(''); return; }
    const res = await api.get(`/api/bom/products/${productId}/details`);
    setOzdisanCode(res.data?.ozdisan_code || '');
  };

  const openComponentForm = (component) => {
    setComponentCategoryFilter('');
    if (component) {
      setEditingComponent(component);
      setComponentForm({
        component_product_id: component.component_product_id,
        quantity: component.quantity,
        unit: component.unit || '',
        reference_designator: component.reference_designator || '',
        sort_order: component.sort_order,
        notes: component.notes || '',
      });
      fetchOzdisanCode(component.component_product_id);
    } else {
      setEditingComponent(null);
      setComponentForm(emptyComponent);
      setOzdisanCode('');
    }
    setShowComponentForm(true);
  };

  const handleComponentProductChange = (productId) => {
    setComponentForm({...componentForm, component_product_id: productId});
    fetchOzdisanCode(productId);
  };

  const handleComponentSubmit = async (e) => {
    e.preventDefault();
    try {
      const componentProductId = editingComponent ? editingComponent.component_product_id : parseInt(componentForm.component_product_id);
      const payload = {
        ...componentForm,
        component_product_id: componentProductId,
        quantity: parseFloat(componentForm.quantity),
        sort_order: parseInt(componentForm.sort_order) || 0,
      };
      if (editingComponent) {
        delete payload.component_product_id;
        await api.put(`/api/bom/assemblies/${id}/components/${editingComponent.id}`, payload);
      } else {
        await api.post(`/api/bom/assemblies/${id}/components`, payload);
      }
      await api.put(`/api/bom/products/${componentProductId}/details`, { ozdisan_code: ozdisanCode || null });
      setShowComponentForm(false);
      setEditingComponent(null);
      setComponentForm(emptyComponent);
      setOzdisanCode('');
      fetchAll();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const handleComponentDelete = async (componentId) => {
    if (!window.confirm('Bileşeni silmek istediğinize emin misiniz?')) return;
    await api.delete(`/api/bom/assemblies/${id}/components/${componentId}`);
    fetchAll();
  };

  if (!product) return null;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Link to="/assemblies" className="text-blue-600 hover:text-blue-800 text-sm">← Montajlar</Link>
      </div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{product.name}</h2>
          <p className="text-sm text-gray-500 font-mono">{product.code}</p>
        </div>
        <button onClick={openCloneForm} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 font-medium text-sm">
          📋 Kopyala
        </button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold mb-4">BOM Özellikleri</h3>
          <form onSubmit={handleDetailsSubmit}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Üretici</label>
                <input value={details.manufacturer || ''} onChange={e => setDetails({...details, manufacturer: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Üretici Parça No</label>
                <input value={details.manufacturer_part_number || ''} onChange={e => setDetails({...details, manufacturer_part_number: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Paket Tipi</label>
                <input value={details.package_type || ''} onChange={e => setDetails({...details, package_type: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Malzeme</label>
                <input value={details.material || ''} onChange={e => setDetails({...details, material: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ağırlık</label>
                <input type="number" step="0.001" value={details.weight ?? ''} onChange={e => setDetails({...details, weight: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Boyutlar</label>
                <input value={details.dimensions || ''} onChange={e => setDetails({...details, dimensions: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Voltaj Değeri</label>
                <input value={details.voltage_rating || ''} onChange={e => setDetails({...details, voltage_rating: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tolerans</label>
                <input value={details.tolerance || ''} onChange={e => setDetails({...details, tolerance: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Yaşam Döngüsü</label>
                <input value={details.lifecycle_status || ''} onChange={e => setDetails({...details, lifecycle_status: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="active / nrnd / obsolete" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Revizyon</label>
                <input value={details.revision || ''} onChange={e => setDetails({...details, revision: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Datasheet URL</label>
                <input value={details.datasheet_url || ''} onChange={e => setDetails({...details, datasheet_url: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Not</label>
                <textarea value={details.notes || ''} onChange={e => setDetails({...details, notes: e.target.value})} className="w-full border rounded px-3 py-2" rows={2} />
              </div>
            </div>
            <button type="submit" className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium text-sm">Kaydet</button>
          </form>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold mb-4">Montaj Ağacı</h3>
          {tree.length === 0 ? (
            <p className="text-gray-400 text-sm">Henüz bileşen eklenmemiş</p>
          ) : (
            <ComponentTree nodes={tree} />
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <div className="flex justify-between items-center gap-3 mb-4">
          <h3 className="text-lg font-bold">Bileşenler</h3>
          <div className="flex items-center gap-3">
            <input
              value={componentSearch}
              onChange={e => setComponentSearch(e.target.value)}
              placeholder="🔍 Kod, ad veya referans ara..."
              className="border rounded px-3 py-2 text-sm w-64"
            />
            <button onClick={handleExcelDownload} disabled={components.length === 0} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50">
              📥 Excel İndir
            </button>
            <button onClick={() => openComponentForm(null)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium text-sm">
              + Bileşen Ekle
            </button>
          </div>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableTh label="Kod" sortKey="product_code" sort={componentSort} onSort={toggleComponentSort} />
              <SortableTh label="Ad" sortKey="product_name" sort={componentSort} onSort={toggleComponentSort} />
              <SortableTh label="Miktar" sortKey="quantity" sort={componentSort} onSort={toggleComponentSort} align="right" />
              <SortableTh label="Referans" sortKey="reference_designator" sort={componentSort} onSort={toggleComponentSort} />
              <th className="text-right px-4 py-2 text-sm font-medium text-gray-500">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {sortedComponents.map(c => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm font-mono text-gray-600">{c.product_code}</td>
                <td className="px-4 py-2 font-medium text-gray-800">
                  {c.is_assembly ? <Link to={`/assemblies/${c.component_product_id}`} className="text-purple-600 hover:text-purple-800">{c.product_name} 🧩</Link> : c.product_name}
                </td>
                <td className="px-4 py-2 text-right text-gray-800">{c.quantity} {c.unit}</td>
                <td className="px-4 py-2 text-gray-600">{c.reference_designator || '-'}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => openComponentForm(c)} className="text-blue-600 hover:text-blue-800 font-medium text-sm mr-2">Düzenle</button>
                  <button onClick={() => handleComponentDelete(c.id)} className="text-red-500 hover:text-red-700 font-medium text-sm">Sil</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sortedComponents.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            {components.length === 0 ? 'Henüz bileşen eklenmemiş' : 'Aramayla eşleşen bileşen yok'}
          </div>
        )}
      </div>

      {showComponentForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-screen overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">{editingComponent ? 'Bileşen Düzenle' : 'Bileşen Ekle'}</h3>
            <form onSubmit={handleComponentSubmit}>
              <div className="grid grid-cols-2 gap-4">
                {!editingComponent && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                    <select value={componentCategoryFilter} onChange={e => { setComponentCategoryFilter(e.target.value); setComponentForm({...componentForm, component_product_id: ''}); }} className="w-full border rounded px-3 py-2">
                      <option value="">Tüm Kategoriler</option>
                      {productCategories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ürün *</label>
                  <select required disabled={!!editingComponent} value={componentForm.component_product_id} onChange={e => handleComponentProductChange(e.target.value)} className="w-full border rounded px-3 py-2 disabled:bg-gray-100">
                    <option value="">Seçiniz</option>
                    {componentCandidates.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}{p.is_assembly ? ' 🧩' : ''}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ozdisan Kodu</label>
                  <input value={ozdisanCode} onChange={e => setOzdisanCode(e.target.value)} className="w-full border rounded px-3 py-2" placeholder="Ozdisan.com stok kodu" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Miktar *</label>
                  <input required type="number" step="0.001" min="0.001" value={componentForm.quantity} onChange={e => setComponentForm({...componentForm, quantity: e.target.value})} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Birim</label>
                  <input value={componentForm.unit} onChange={e => setComponentForm({...componentForm, unit: e.target.value})} className="w-full border rounded px-3 py-2" placeholder={product.unit} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Referans</label>
                  <input value={componentForm.reference_designator} onChange={e => setComponentForm({...componentForm, reference_designator: e.target.value})} className="w-full border rounded px-3 py-2" placeholder="R1, C2..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sıra</label>
                  <input type="number" value={componentForm.sort_order} onChange={e => setComponentForm({...componentForm, sort_order: e.target.value})} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Not</label>
                  <textarea value={componentForm.notes} onChange={e => setComponentForm({...componentForm, notes: e.target.value})} className="w-full border rounded px-3 py-2" rows={2} />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium">Kaydet</button>
                <button type="button" onClick={() => setShowComponentForm(false)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCloneForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-1">Montajı Kopyala</h3>
            <p className="text-sm text-gray-500 mb-4">
              Bileşenler yeni montaja kopyalanır. Alt montajlar (varsa) kopyalanmaz, orijinaline referans olarak kalır.
            </p>
            <form onSubmit={handleCloneSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Kod *</label>
                <input required value={cloneForm.code} onChange={e => setCloneForm({...cloneForm, code: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Ad *</label>
                <input required value={cloneForm.name} onChange={e => setCloneForm({...cloneForm, name: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" disabled={cloning} className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
                  {cloning ? 'Kopyalanıyor...' : 'Kopyala'}
                </button>
                <button type="button" onClick={() => setShowCloneForm(false)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
